import { Fragment, useEffect, useMemo, useState } from "react";
import { Link } from "react-router";

import type { AlertDelivery, AlertStatus } from "../lib/api";
import { formatDateTime, plural } from "../lib/format";
import { useAlertLog, useEventKinds } from "../lib/queries";
import { ListSkeleton } from "./Loading";
import { Pager } from "./Pager";

const PAGE_SIZE = 20;

export const ALERT_STATUS_LABELS: Record<AlertStatus, string> = {
  sent: "Envoyé",
  failed: "Échec d'envoi",
  deferred: "Différé",
  soft_bounce: "Rebond temporaire",
  delivered: "Délivré",
  opened: "Ouvert",
  clicked: "Cliqué",
  hard_bounce: "Adresse inexistante",
  invalid_email: "Adresse invalide",
  blocked: "Bloqué",
  spam: "Signalé comme spam",
  unsubscribed: "Désinscrit",
  error: "Erreur",
};

// Red is reserved for company severity: only delivery failures borrow the error tone.
const FAILED = new Set<AlertStatus>(["failed", "hard_bounce", "invalid_email", "blocked", "spam", "error"]);
const REACHED = new Set<AlertStatus>(["delivered", "opened", "clicked"]);

function StatusChip({ status }: { status: AlertStatus }) {
  const tone = FAILED.has(status) ? " chip--danger" : REACHED.has(status) ? " chip--active" : "";
  return <span className={`chip${tone}`}>{ALERT_STATUS_LABELS[status] ?? status}</span>;
}

function Details({ delivery, kindLabel }: { delivery: AlertDelivery; kindLabel: (kind: string) => string }) {
  return (
    <div className="alert-log__details">
      <p className="field__hint">
        Statut « {(ALERT_STATUS_LABELS[delivery.status] ?? delivery.status).toLowerCase()} » le{" "}
        {formatDateTime(delivery.status_at)}
        {delivery.status_detail && <> : {delivery.status_detail}</>}
      </p>
      {delivery.events.length ? (
        <ul className="alert-log__events">
          {delivery.events.map((event, i) => (
            <li key={event.event_id ?? `${event.siren}-${i}`}>
              <Link to={`/entreprise/${event.siren}`}>{event.company ?? `SIREN ${event.siren}`}</Link>
              <span className={`tag tag--${event.severity}`}>{kindLabel(event.kind)}</span>
              <span className="alert-log__title">{event.title}</span>
            </li>
          ))}
        </ul>
      ) : (
        <p className="field__hint">Le détail des événements n'est plus disponible.</p>
      )}
    </div>
  );
}

export function AlertLog() {
  const [page, setPage] = useState(1);
  const [open, setOpen] = useState<Set<number>>(() => new Set());
  const log = useAlertLog(page, PAGE_SIZE);
  const kinds = useEventKinds();

  const labels = useMemo(() => new Map((kinds.data ?? []).map((k) => [k.kind, k.label])), [kinds.data]);
  const kindLabel = (kind: string) => labels.get(kind) ?? kind;

  // The log shrank under us (retention purge, account emptied): back to the last page.
  const shrunk = log.data && !log.isPlaceholderData && !log.data.items.length && page > 1;
  const lastPage = Math.max(1, Math.ceil((log.data?.total ?? 0) / PAGE_SIZE));
  useEffect(() => {
    if (shrunk) setPage(lastPage);
  }, [shrunk, lastPage]);

  if (log.isPending) return <ListSkeleton rows={3} />;
  if (log.isError) return <div className="notice notice--error">{log.error.message}</div>;

  const { items, total } = log.data;
  if (!total) {
    return (
      <p className="alert-log__empty">
        Aucune alerte envoyée pour l'instant. Dès qu'un événement sera publié pour vos entreprises, l'e-mail
        apparaîtra ici avec son statut de distribution.
      </p>
    );
  }

  const toggle = (id: number) =>
    setOpen((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  return (
    <div className="alert-log">
      <div className="table-wrap">
        <table className="data-table">
          <caption className="visually-hidden">Alertes envoyées, de la plus récente à la plus ancienne</caption>
          <thead>
            <tr>
              <th scope="col">Date</th>
              <th scope="col">Destinataire</th>
              <th scope="col">Objet</th>
              <th scope="col">Statut</th>
              <th scope="col" className="num">
                Événements
              </th>
            </tr>
          </thead>
          <tbody aria-busy={log.isPlaceholderData || undefined}>
            {items.map((delivery) => {
              const expanded = open.has(delivery.id);
              const detailsId = `alert-log-${delivery.id}`;
              return (
                <Fragment key={delivery.id}>
                  <tr className={expanded ? "is-expanded" : undefined}>
                    <td className="num">{formatDateTime(delivery.sent_at)}</td>
                    <td className="alert-log__recipient">{delivery.recipient}</td>
                    <td>
                      <button
                        type="button"
                        className="button button--link alert-log__toggle"
                        aria-expanded={expanded}
                        aria-controls={detailsId}
                        onClick={() => toggle(delivery.id)}
                      >
                        {delivery.subject}
                      </button>
                    </td>
                    <td>
                      <StatusChip status={delivery.status} />
                    </td>
                    <td className="num">
                      {plural(delivery.event_count, "événement", "événements")}
                      <span className="alert-log__companies">
                        {plural(delivery.company_count, "entreprise", "entreprises")}
                      </span>
                    </td>
                  </tr>
                  {expanded && (
                    <tr id={detailsId} className="alert-log__row-details">
                      <td colSpan={5}>
                        <Details delivery={delivery} kindLabel={kindLabel} />
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      </div>
      {total > PAGE_SIZE && (
        <Pager
          page={page}
          pageSize={PAGE_SIZE}
          total={total}
          onPage={(next) => {
            setPage(next);
            setOpen(new Set());
          }}
        />
      )}
      <p className="field__hint">
        Les statuts « Ouvert » et « Cliqué » sont indicatifs : certaines messageries ouvrent les e-mails
        automatiquement, d'autres bloquent ce suivi.
      </p>
    </div>
  );
}
